/**
 * Wording for the phone Pour Dashboard: values with their units, limit and
 * breach lines, status words and short times, so the Overview cards, the
 * Location tab and the strip say the same thing the same way.
 *
 * Pure functions over a `t` (every string has an English default; no em
 * dashes). The rules themselves live in mobileDashboardModel.
 */
import type { LimitBreach, LocationStatus, Pour2Limit, TempLimit } from '../../../services/pours';
import { formatInTimeZone, parseUtc, type IsoLike } from '../../../utils/datetime';
import { isHumidityLocation } from '../../../utils/locationChannels';
import type { OnlineLevel } from '../../../utils/onlineStatus';
import { formatDuration, formatIn, type TranslateFn } from '../../../utils/relativeTime';
import { LIMIT_OPERATOR_SYMBOLS } from '../limitLines';
import { DEFAULT_SCALE_MAX } from '../TemperatureProfileBar';
import { isLowerOperator, type LocationAlert, type StaleOver } from './mobileDashboardModel';

/** Comparison symbols as people read them ('>=' reads '≥'). */
const DISPLAY_SYMBOLS: Record<string, string> = { '>': '>', '>=': '≥', '<': '<', '<=': '≤' };

/** A limit operator ('>=', or a stored code '02' / 'Upper') as shown:
 *  '≥'. Unknown operators read '≥', as the backend compares them. */
export const operatorText = (operator: string | null | undefined): string => {
  const op = LIMIT_OPERATOR_SYMBOLS[operator ?? ''] ?? operator ?? '';
  return DISPLAY_SYMBOLS[op] ?? '≥';
};

/** Epoch ms of a backend time (naive UTC), or null. */
export const toEpochMs = (value: IsoLike): number | null => {
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** A number to 1 dp without a trailing ".0" on limits ("75", "20.5"). */
export const limitNumber = (value: number): string => String(Number(value.toFixed(1)));

/** A location's display name, as the desktop list names it. */
export const locationLabel = (loc: Pick<LocationStatus, 'id' | 'name' | 'logger_code'>): string =>
  loc.name || loc.logger_code || `Location ${loc.id.slice(-8)}`;

/** A channel's value with its unit: "42.3 °C", or "64.0 %RH" on a humidity
 *  channel; '-' without a value. */
export const channelValue = (
  loc: Pick<LocationStatus, 'channel_type'>,
  value: number | null | undefined,
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  return isHumidityLocation(loc as LocationStatus) ? `${value.toFixed(1)} %RH` : `${value.toFixed(1)} °C`;
};

/** "22 Oct, 16:00" in the pour's timezone; '' when empty. */
export const shortDateTime = (value: IsoLike, timezoneIana?: string | null): string =>
  formatInTimeZone(value, timezoneIana, {
    day: 'numeric',
    month: 'short',
    year: undefined,
    hour: '2-digit',
    minute: '2-digit',
  });

/** The bar scale of the pour's profile bars: its "Temperature Bar
 *  Indicator" row (Criteria '03'), else the default, as desktop. */
export const barScaleMax = (limits: ReadonlyArray<Pour2Limit>): number => {
  const bar = limits.find((l) => l.Criteria === '03');
  const v = bar ? Number(bar.LimitsTemp) : NaN;
  return v > 0 ? v : DEFAULT_SCALE_MAX;
};

/** A limit as words: "Max Temp (≥ 75 °C)", or "limit (≥ 75 °C)" without a
 *  name. `unit` '' leaves the unit out. */
export const limitText = (
  limit: { name: string | null; operator: string | null | undefined; value: number },
  t: TranslateFn,
  unit: string = '°C',
): string =>
  t('mobile.dashboard.limitText', {
    defaultValue: '{{name}} ({{op}} {{value}}{{unit}})',
    name: limit.name || t('mobile.dashboard.limitFallback', { defaultValue: 'limit' }),
    op: operatorText(limit.operator),
    value: limitNumber(limit.value),
    unit: unit ? ` ${unit}` : '',
  });

/** The visible caption under a profile bar: "Limit 75 °C". */
export const limitCaptionText = (limit: TempLimit, t: TranslateFn): string =>
  t('mobile.dashboard.limitCaption', {
    defaultValue: 'Limit {{op}}{{value}} °C',
    op: isLowerOperator(limit.operator) ? `${operatorText(limit.operator)} ` : '',
    value: limitNumber(limit.value),
  });

/** The caption of every limit a profile bar marks, so none rests on the
 *  markers' hover titles: "Limit 75 °C" for one, else each by name from
 *  the lowest, "Limits: Min ≤ 5 °C · Warn 70 °C · Max Temp 75 °C". '' with
 *  none. */
export const limitsCaptionText = (limits: ReadonlyArray<TempLimit>, t: TranslateFn): string => {
  if (limits.length === 0) return '';
  if (limits.length === 1) return limitCaptionText(limits[0], t);
  const items = [...limits]
    .sort((a, b) => a.value - b.value)
    .map((limit) =>
      t('mobile.dashboard.limitItem', {
        defaultValue: '{{name}} {{op}}{{value}} °C',
        name: limit.name || t('mobile.charts.limit', { defaultValue: 'Limit' }),
        op: isLowerOperator(limit.operator) ? `${operatorText(limit.operator)} ` : '',
        value: limitNumber(limit.value),
      }),
    );
  return t('mobile.dashboard.limitsCaption', { defaultValue: 'Limits: {{list}}', list: items.join(' · ') });
};

/**
 * One limit broken now, as a line: "Over Max Temp (≥ 75 °C) for 2 h 10 min",
 * "Under Min Temp (≤ 5 °C)", or for a differential "Over Diff Limit (≥ 20
 * °C) vs Surface". The duration runs from the breach start to `refNow`, the
 * time of the data.
 */
export const breachText = (breach: LimitBreach, t: TranslateFn, refNow: number): string => {
  const limit = limitText({ name: breach.limit_name, operator: breach.operator, value: breach.limit_value }, t);
  const lower = isLowerOperator(breach.operator);
  const head = breach.kind === 'differential'
    ? lower
      ? t('mobile.dashboard.breachDiffUnder', { defaultValue: 'Under {{limit}} vs {{partner}}', limit, partner: breach.partner_name || '-' })
      : t('mobile.dashboard.breachDiffOver', { defaultValue: 'Over {{limit}} vs {{partner}}', limit, partner: breach.partner_name || '-' })
    : lower
      ? t('mobile.dashboard.breachUnder', { defaultValue: 'Under {{limit}}', limit })
      : t('mobile.dashboard.breachOver', { defaultValue: 'Over {{limit}}', limit });
  return [head, breachDuration(breach, t, refNow)].filter(Boolean).join(' ');
};

/** "for 2 h 10 min", "for more than 48 h", or '' when the start is unknown. */
export const breachDuration = (breach: LimitBreach, t: TranslateFn, refNow: number): string => {
  if (breach.since_capped) return t('mobile.dashboard.breachCapped', { defaultValue: 'for more than 48 h' });
  const since = toEpochMs(breach.since);
  if (since === null) return '';
  return t('mobile.dashboard.breachFor', { defaultValue: 'for {{duration}}', duration: formatDuration(refNow - since, t) });
};

/** An Offline location whose last reading broke a limit: "Offline · last
 *  reading 78.3 °C was over Max Temp (3 h ago)". */
export const staleOverText = (stale: StaleOver, t: TranslateFn, ago: string): string => {
  const limit = stale.limitName || t('mobile.dashboard.limitFallback', { defaultValue: 'limit' });
  const value = `${stale.value.toFixed(1)} °C`;
  const lower = isLowerOperator(stale.operator);
  const text = stale.kind === 'differential'
    ? lower
      ? t('mobile.dashboard.staleDiffUnder', { defaultValue: 'Offline · last differential {{value}} was under {{limit}}', value, limit })
      : t('mobile.dashboard.staleDiffOver', { defaultValue: 'Offline · last differential {{value}} was over {{limit}}', value, limit })
    : lower
      ? t('mobile.dashboard.staleUnder', { defaultValue: 'Offline · last reading {{value}} was under {{limit}}', value, limit })
      : t('mobile.dashboard.staleOver', { defaultValue: 'Offline · last reading {{value}} was over {{limit}}', value, limit });
  return ago ? `${text} (${ago})` : text;
};

/** A location's status word for its pill: "Over limit", "Offline",
 *  "Reporting", "Starts in 2 h", "Ended", ... A location within its limits
 *  or with no limit to judge has no word (''): the phone shows its readings
 *  only. */
export const alertWord = (
  alert: LocationAlert,
  t: TranslateFn,
  { startsAt, now }: { startsAt?: IsoLike; now?: number } = {},
): string => {
  switch (alert) {
    case 'over_limit':
      return t('mobile.dashboard.alert.overLimit', { defaultValue: 'Over limit' });
    case 'under_limit':
      return t('mobile.dashboard.alert.underLimit', { defaultValue: 'Under limit' });
    case 'offline':
      return t('mobile.dashboard.alert.offline', { defaultValue: 'Offline' });
    case 'delayed':
      return t('mobile.dashboard.alert.delayed', { defaultValue: 'Delayed' });
    case 'sensor':
      return t('mobile.dashboard.alert.sensor', { defaultValue: 'Sensor issue' });
    case 'no_limits':
    case 'ok':
      return '';
    case 'reporting':
      return t('mobile.dashboard.alert.reporting', { defaultValue: 'Reporting' });
    case 'awaiting':
      return t('mobile.dashboard.alert.awaiting', { defaultValue: 'Awaiting' });
    case 'scheduled': {
      const when = startsAt != null && now != null ? formatIn(startsAt, now, t) : '';
      return when
        ? t('mobile.dashboard.alert.startsIn', { defaultValue: 'Starts {{when}}', when })
        : t('mobile.dashboard.alert.scheduled', { defaultValue: 'Scheduled' });
    }
    case 'ended':
      return t('mobile.dashboard.alert.ended', { defaultValue: 'Ended' });
    default:
      return '';
  }
};

/** The freshness word of a level: "Current", "Delayed", "Offline",
 *  "Logging ended", "Scheduled". */
export const freshnessWord = (level: OnlineLevel, t: TranslateFn, awaiting?: boolean): string => {
  if (awaiting) return t('mobile.dashboard.fresh.awaiting', { defaultValue: 'Awaiting first reading' });
  switch (level) {
    case 'online':
      return t('mobile.dashboard.fresh.online', { defaultValue: 'Current' });
    case 'delayed':
      return t('mobile.dashboard.fresh.delayed', { defaultValue: 'Delayed' });
    case 'offline':
      return t('mobile.dashboard.fresh.offline', { defaultValue: 'Offline' });
    case 'ended':
      return t('mobile.dashboard.fresh.ended', { defaultValue: 'Logging ended' });
    default:
      return t('mobile.dashboard.fresh.scheduled', { defaultValue: 'Scheduled' });
  }
};

/** The longest common start of some names, cut back to a whole word and
 *  without trailing separators ("Castle Hill" from "Castle Hill Temp" and
 *  "Castle Hill Humidity"); '' when it is shorter than 3 characters. */
export const commonNameStart = (names: ReadonlyArray<string>): string => {
  if (names.length === 0) return '';
  let prefix = names[0];
  names.slice(1).forEach((name) => {
    let i = 0;
    while (i < prefix.length && i < name.length && prefix[i] === name[i]) i += 1;
    prefix = prefix.slice(0, i);
  });
  if (names.length > 1 && names.some((n) => n.length > prefix.length && /\w/.test(n[prefix.length] ?? ''))) {
    // Cut a part-word back to the last whole word.
    const space = prefix.lastIndexOf(' ');
    prefix = space > 0 ? prefix.slice(0, space) : '';
  }
  prefix = prefix.replace(/[\s\-·:,_(]+$/, '').trim();
  return prefix.length >= 3 ? prefix : '';
};
